import { DynamicFormProvider } from "@scenes/bulk-request/context/DynamicFormContext";
import { SelectedItemsProvider } from "../../contexts/SelectedItemsContext";
import { Platform, ScrollView } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import Container from "@components/atoms/Container";
import Spacer from "@components/atoms/Spacer";
import RequestStatusCard from "./RequestStatusCard";
import CompanyDetailsCard from "./utils/CompanyDetailsCard";
import StoreCustomerAttributes from "./StoreAttributesCard";
import CommentSection from "./CommentSection";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBText } from "@components/atoms";
import { ProductCategory, ProductTypeCommon, ViewType } from "@utils/constants";
import { VDBIcons } from "@utils/VDBIcons";
import RequestViewParentCard from "@components/organisms/RequestViewParentCard";
import { getBoxShadow } from "@utils/product/productValidators";
import TotalPriceCard from "./TotalPriceCard";
import styled from "styled-components/native";
import { DefaultThemeValueType } from "../../../styled";
import { getOrderTypeDisplayRows, parseOrderComments } from "./utils/utils";
import React from "react";
import { SearchFilterTypeEnumLowercase } from "@components/atoms/Filters/utils";
import PaymentDetails from "./utils/PaymentDetails";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ShippingLocation from "./utils/ShippingLocationRequest";
import { useTranslation } from "react-i18next";
import { useOrderCart } from "@redux/slices/globalSlice";
import ShippingInformation from "./utils/ShippingInformation";
import { extractWireTransferSection } from "@scenes/configurator/OrderCart/DynamicSuccessPopUp";
import { getProductName } from "@utils/product/productNames";
import { handleDeepLink } from "@utils/product/productFormatters";
import LineViewContainer from "./utils/LineView";
import QuoteSenderDetailsCard from "./utils/QuoteSenderDetailsCard";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import SalesPersonButtonsContainer from "./utils/SalesPersonButtonsContainer";
import ButtonsContainer from "./utils/ButtonsContainer";
import ReasonContainer from "./utils/ReasonContainer";
import { messageType } from "@components/molecules/PopUp/types";

type OrderRequestScreenProps = {
  requestName?: string;
  theme?: DefaultThemeValueType;
  stockItemRequest?: any;
  isCustomApp: boolean;
  showCompanyDetails?: any;
  productType?: ProductCategory;
  iconData?: any;
  typography?: any;
  validValue?: any;
  productPrefix?: string;
  hasChildren?: boolean;
  setDisabledButton?: any;
  modifyRequest?: () => void;
  disabledButton?: boolean;
  selectedItems?: any;
  isHideOtherButtonVisible?: boolean;
  isBuilderItem?: boolean;
};

const OrderRequestScreen = ({
  requestName,
  theme,
  stockItemRequest,
  isCustomApp,
  showCompanyDetails,
  productType,
  iconData,
  typography,
  validValue,
  hasChildren,
  setDisabledButton,
  modifyRequest,
  disabledButton = false,
  selectedItems,
  isHideOtherButtonVisible,
  isBuilderItem,
}: OrderRequestScreenProps) => {
  const storeCustomerAttributes = parseOrderComments(
    stockItemRequest?.order_comments
  );
  const isLooseParcel = stockItemRequest?.[productType]?.diamond_category
    ? stockItemRequest?.[productType]?.diamond_category ===
      SearchFilterTypeEnumLowercase.LOOSE_PARCEL
    : false;
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const userInfo = useUserInfo();
  const orderCart = useOrderCart();
  const priceMode = userInfo?.user?.price_mode;
  const paymentDetails =
    unifiedProductPagesSlice?.unifiedProductData?.[
      productType?.productType
    ]?.requests?.payment_details?.price_modes.includes(priceMode);
  const { t } = useTranslation();
  const shippingInformation = stockItemRequest?.shipping_instructions;
  const wireTransferConfig = orderCart?.successOfflinePopupConfig;
  const wireTransferDetails = extractWireTransferSection(
    wireTransferConfig,
    stockItemRequest?.wire_transfer_details
  );
  const isSalesPerson =
    !userInfo.forceGreenPriceProfile() &&
    userInfo.priceProfileVisibility() &&
    !userInfo.isPriceModeLock() &&
    priceMode === PriceModes.ORANGE;

  const showButtons =
    userInfo?.getFeatureSettings()?.enable_order_cart_approval_workflow ===
      true &&
    isSalesPerson &&
    stockItemRequest?.view_type === ViewType.SELLER;
  const isQuoteOrder =
    String(stockItemRequest?.order_type ?? "").toLowerCase() === "quote";
  const isOrderCartQuote =
    userInfo.enableOrderCartQuote() === true && isQuoteOrder;
  const isOrderCartQuoteSender =
    isOrderCartQuote && stockItemRequest?.view_type === ViewType.BUYER;
  const isOrderCartQuoteReceiver =
    isQuoteOrder && stockItemRequest?.view_type === ViewType.SELLER;
  const quoteCommentAttributes = storeCustomerAttributes?.attributes ?? {};
  const readableText = (value: unknown) => {
    if (value == null || typeof value === "object") return "";
    const text = String(value).trim();
    if (!text || text.includes("[object Object]")) return "";
    return text;
  };
  const quoteAttributeValue = (matcher: (key: string) => boolean) => {
    const match = Object.entries(quoteCommentAttributes).find(
      ([key, value]) => {
        const normalizedKey = key.toLowerCase().replace(/[\s_]/g, "");
        if (normalizedKey === "companylocations") return false;
        return matcher(normalizedKey) && !!readableText(value);
      }
    );
    return readableText(match?.[1]);
  };
  const quoteCompanyLocation =
    readableText(stockItemRequest?.recipient_company_location) ||
    quoteAttributeValue(
      (key) => key === "recipientcompanylocation" || key === "companylocation"
    );
  const supplierLocation =
    stockItemRequest?.buyer_location ?? stockItemRequest?.buyer_details ?? {};
  const supplierContact = stockItemRequest?.buyer ?? {};
  const supplierEmail = String(
    supplierLocation?.contact_email || supplierContact?.email || ""
  )
    .replaceAll(";", "; ")
    .trim();
  const orderTypeRows =
    isOrderCartQuoteSender || isOrderCartQuoteReceiver
      ? getOrderTypeDisplayRows(
          quoteCommentAttributes,
          orderCart?.hide_additional_form
            ? {}
            : orderCart?.additional_details_form,
          (key, fallback) => t(key, fallback ?? key)
        )
      : [];
  const quoteComment =
    isOrderCartQuoteSender || isOrderCartQuoteReceiver
      ? storeCustomerAttributes?.comments &&
        storeCustomerAttributes.comments !== "-"
        ? storeCustomerAttributes.comments
        : stockItemRequest?.order_comments &&
          !String(stockItemRequest.order_comments).includes("\n")
        ? stockItemRequest.order_comments
        : ""
      : storeCustomerAttributes?.comments;
  const showQuoteSenderButtons =
    isOrderCartQuoteSender && priceMode === PriceModes.WHITE;
  const showQuoteReceiverButtons = isOrderCartQuoteReceiver;
  const supplierDetailsLabel = t(
    "requestHistoryDetail.suppliersdetails.text",
    "Suppliers Details"
  );
  const resolvedSupplierDetailsLabel = supplierDetailsLabel
    .toLowerCase()
    .includes("receiver")
    ? "Suppliers Details"
    : supplierDetailsLabel;

  const hideBecauseMemo =
    stockItemRequest?.memo_status &&
    stockItemRequest?.view_type === ViewType.BUYER;
  const actionComment = [
    stockItemRequest?.action_comment,
    ...(stockItemRequest?.children ?? []).map(
      (item: { action_comment?: string }) => item?.action_comment
    ),
  ]
    .map((value) => String(value ?? "").trim())
    .find((value) => !!value);
  const quoteActionStatuses = [
    stockItemRequest?.track_status_key,
    ...(stockItemRequest?.children ?? []).map(
      (item: { status?: string }) => item?.status
    ),
  ].map((status) =>
    String(status ?? "")
      .trim()
      .toLowerCase()
  );
  const isDeclineReason = quoteActionStatuses.some(
    (status) => status === "decline" || status === "declined"
  );
  const isWithdrawReason = quoteActionStatuses.some(
    (status) => status === "withdraw" || status === "withdrawn"
  );
  const showQuoteActionReason =
    (isOrderCartQuoteSender || isOrderCartQuoteReceiver) &&
    !!actionComment &&
    (isDeclineReason || isWithdrawReason);
  const quoteActionReasonTitle =
    isDeclineReason && !isWithdrawReason
      ? t(
          "requestHistoryDetail.quote.declinedReason.text",
          "Reason for Declined"
        )
      : t(
          "requestHistoryDetail.quote.withdrawReason.text",
          "Reason for Withdrawal"
        );

  const renderButtons = () => (
    <StyledButtonsContainer
      stockItemRequest={stockItemRequest}
      productType={productType}
      disabledButton={disabledButton}
      modifyRequest={modifyRequest}
      selectedItems={selectedItems}
      isHideOtherButtonVisible={isHideOtherButtonVisible}
      isBuilderItem={isBuilderItem}
    />
  );

  const renderQuoteSenderButtons = () => (
    <QuoteSenderButtons
      stockItemRequest={{
        ...stockItemRequest,
        request_type: "quote",
        view_type: ViewType.BUYER,
        status: "pending",
      }}
      productType={productType}
      modifyRequest={modifyRequest}
      disabledButton={disabledButton}
      selectedItems={selectedItems}
      isHideOtherButtonVisible={false}
      isBuilderItem={isBuilderItem}
      showOrderCartQuoteSenderActions
      onSubmitChange={() => {}}
    />
  );

  const renderQuoteReceiverButtons = () => (
    <ButtonsContainer
      stockItemRequest={stockItemRequest}
      productType={productType}
      modifyRequest={modifyRequest}
      disabledButton={disabledButton}
      selectedItems={selectedItems}
      isHideOtherButtonVisible={false}
      isBuilderItem={isBuilderItem}
      showOrderCartQuoteReceiverActions
      onSubmitChange={() => {}}
    />
  );

  return (
    <SelectedItemsProvider>
      <DynamicFormProvider>
        <MainContainer>
          <ScrollView
            style={{
              flex: 1,
              paddingHorizontal: theme.isMobile
                ? SpaceType.Basic
                : SpaceType.Double,
            }}
          >
            <Container maxWidth={1008} noPadding>
              <Spacer size={SpaceType.Double} />
              {(isOrderCartQuoteSender || isOrderCartQuoteReceiver) &&
                !theme.isMobile && (
                  <>
                    <QuoteTitleRow>
                      <VDBIcon
                        name={VDBIcons.QUOTE}
                        size={24}
                        color={theme["main-01"]}
                        variant={IconsVariant.VDBCustom}
                      />
                      <VDBText
                        color={theme["core-05"]}
                        type={typography["H2-M"]}
                      >
                        {t(
                          "requestHistoryDetail.quoteDetails.title.text",
                          "Quote Details"
                        )}
                      </VDBText>
                    </QuoteTitleRow>
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
              <RequestStatusCard
                stockItemRequest={stockItemRequest}
                isCustomApp={isCustomApp}
                requestName={requestName}
                displayAsQuote={
                  isOrderCartQuoteSender || isOrderCartQuoteReceiver
                }
                quoteDateLabel={
                  isOrderCartQuoteReceiver
                    ? t("requestHistoryDetail.receivedOn.text", "Received On")
                    : undefined
                }
                quoteIdValue={
                  isOrderCartQuoteReceiver ? stockItemRequest?.id : undefined
                }
              />
              <Spacer size={SpaceType.Double} />
              {isOrderCartQuoteReceiver ? (
                <>
                  <QuoteSenderDetailsCard
                    label={resolvedSupplierDetailsLabel}
                    companyName={
                      supplierLocation?.company_name ||
                      supplierContact?.company_name ||
                      supplierLocation?.location_name
                    }
                    companyLocation={[
                      supplierLocation?.city,
                      supplierLocation?.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    contactName={
                      supplierLocation?.contact_person || supplierContact?.name
                    }
                    email={supplierEmail}
                    officePhone={supplierLocation?.office_phone}
                    officePhoneCountryCode={
                      supplierLocation?.contact_phone_country_code ||
                      supplierLocation?.office_phone_country_code
                    }
                    mobilePhone={
                      supplierContact?.main_phone ||
                      supplierLocation?.mobile_phone
                    }
                    mobilePhoneCountryCode={
                      supplierContact?.mobile_phone_country_code ||
                      supplierLocation?.mobile_phone_country_code
                    }
                    shareLink={
                      stockItemRequest?.share_link ??
                      stockItemRequest?.shareable_link?.link
                    }
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              ) : isOrderCartQuoteSender ? (
                <>
                  <QuoteSenderDetailsCard
                    companyName={stockItemRequest?.recipient_company_name}
                    companyLocation={quoteCompanyLocation}
                    contactName={stockItemRequest?.recipient_name}
                    email={stockItemRequest?.recipient_email}
                    mobilePhone={stockItemRequest?.recipient_phone_number}
                    mobilePhoneCountryCode={
                      stockItemRequest?.recipient_phone_country_code
                    }
                    shareLink={
                      stockItemRequest?.share_link ??
                      stockItemRequest?.shareable_link?.link
                    }
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              ) : (
                showCompanyDetails && (
                  <>
                    <CompanyDetailsCard
                      stockItemRequest={stockItemRequest}
                      productType={productType}
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )
              )}
              {orderTypeRows.length > 0 && (
                <StoreCustomerAttributes
                  attributes={Object.fromEntries(
                    orderTypeRows.map((row) => [row.label, row.value])
                  )}
                />
              )}
              {!isOrderCartQuoteSender &&
                !isOrderCartQuoteReceiver &&
                Object.keys(stockItemRequest?.payment_details || {}).length >
                  0 &&
                paymentDetails && (
                  <>
                    <PaymentDetails
                      payment_details={stockItemRequest?.payment_details}
                      productType={productType?.productType}
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
              {!isOrderCartQuoteSender &&
                !isOrderCartQuoteReceiver &&
                typeof storeCustomerAttributes?.attributes === "object" && (
                  <StoreCustomerAttributes
                    attributes={storeCustomerAttributes?.attributes}
                  />
                )}
              {Object.keys(stockItemRequest.shipping_location || {}).length >
                0 && (
                <>
                  <ShippingLocation
                    shipping_location={stockItemRequest.shipping_location}
                  />
                  <Spacer size={SpaceType.Double} />
                </>
              )}
              {!isOrderCartQuoteSender &&
                !isOrderCartQuoteReceiver &&
                shippingInformation && (
                  <>
                    <ShippingInformation
                      title={t(
                        "requestHistoryDetail.shippingInformation.title.text",
                        "Shipping Information"
                      )}
                      description={shippingInformation}
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
              {!isOrderCartQuoteSender &&
                !isOrderCartQuoteReceiver &&
                wireTransferDetails?.description && (
                  <>
                    <ShippingInformation
                      title={t(wireTransferDetails?.title)}
                      description={wireTransferDetails?.description}
                    />
                    <Spacer size={SpaceType.Double} />
                  </>
                )}
              <CommentSection requestType={""} comments={quoteComment} />
              <CardContainer>
                <ProductListTopContainer background={productType.theme}>
                  <VDBIcon
                    variant={IconsVariant.VDBCustom}
                    name={iconData}
                    size={SpaceType.Big}
                    color={theme[`${productType.theme}-06`]}
                  />
                  <Spacer size={SpaceType.ExtraSmall} />
                  <VDBText
                    type={typography["H3-M"]}
                    color={theme[`${productType.theme}-06`]}
                  >
                    {t(
                      stockItemRequest?.[productType.productType]?.sub_type ===
                        ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE
                        ? getProductName(ProductCategory.LABGROWN_JEWELRY, t)
                        : stockItemRequest.type_text
                    )}
                  </VDBText>
                </ProductListTopContainer>
                <ItemsContainer>
                  <RequestViewParentCard
                    stockItemRequest={stockItemRequest}
                    itemList={stockItemRequest?.children}
                    productType={productType}
                    validValue={validValue}
                    onClickSupplierInfo={() =>
                      handleDeepLink(stockItemRequest?.deep_link ?? "")
                    }
                    setDisabledButton={setDisabledButton}
                    fromRequestDetail={true}
                    isModifyScreen={true}
                    orderCartRequest={true}
                    quoteItemSelection={
                      isOrderCartQuote || isOrderCartQuoteReceiver
                    }
                  />
                </ItemsContainer>
              </CardContainer>
              <Spacer size={SpaceType.Double} />

              <TotalPriceCard
                stockItemRequest={stockItemRequest}
                productType={productType}
                hasChildren={hasChildren}
                orderCartRequest={true}
                isLooseParcel={isLooseParcel}
              />

              {showQuoteActionReason && (
                <>
                  <Spacer size={SpaceType.Double} />
                  <ReasonContainer
                    type={messageType.Error}
                    title={quoteActionReasonTitle}
                    applyShadow
                  >
                    {actionComment}
                  </ReasonContainer>
                </>
              )}

              {(theme.isTablet || theme.isLargeScreen) &&
                !hideBecauseMemo &&
                (showButtons ||
                  showQuoteSenderButtons ||
                  showQuoteReceiverButtons) && (
                  <>
                    {showQuoteSenderButtons
                      ? renderQuoteSenderButtons()
                      : showQuoteReceiverButtons
                      ? renderQuoteReceiverButtons()
                      : renderButtons()}
                    <Spacer size={SpaceType.Size_80} />
                  </>
                )}

              <Spacer size={SpaceType.ExtraLarge} />
            </Container>
          </ScrollView>

          {theme.isMobile &&
            !hideBecauseMemo &&
            (showButtons ||
              showQuoteSenderButtons ||
              showQuoteReceiverButtons) && (
              <>
                <LineViewContainer background="core-03" marginLeft={false} />
                {showQuoteSenderButtons
                  ? renderQuoteSenderButtons()
                  : showQuoteReceiverButtons
                  ? renderQuoteReceiverButtons()
                  : renderButtons()}
              </>
            )}

          {Platform.OS === "ios" && <FooterSpace />}
        </MainContainer>
      </DynamicFormProvider>
    </SelectedItemsProvider>
  );
};

const QuoteTitleRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Basic}px;
`;

const MainContainer = styled.View`
  flex: 1;
  min-height: 0;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const FooterSpace = styled.View`
  padding: 10px;
  height: ${Platform.OS === "ios" ? 34 : 10}px;
  background-color: ${({ theme }) => theme["core-01"]};
`;

const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const ItemsContainer = styled.View`
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
  padding-right: ${({ theme }) =>
    theme.isMobile ? `${SpaceType.Basic}px` : `${SpaceType.Double}px`};
`;

const ProductListTopContainer = styled.View<{ background: string }>`
  background-color: ${({ background, theme }) => theme[`${background}-03`]};
  padding: ${SpaceType.Extra}px ${SpaceType.Extra}px;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const StyledButtonsContainer = styled(SalesPersonButtonsContainer)`
  border-radius: 8px;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

const QuoteSenderButtons = styled(ButtonsContainer)`
  border-radius: 8px;
  box-shadow: ${({ theme }) => getBoxShadow(theme.Shadows.Gentle)};
`;

export default OrderRequestScreen;
